import { IonIcon } from '@ionic/react';
import { eye, personOutline } from 'ionicons/icons';
import type { Player } from '../../../types/graphql-generated';
import { PlayerCard } from '../components/PlayerCard';

export interface ReceivedOffer {
  id: string;
  amount: number;
  bidderName: string;
  bidderMemberId?: string;
  onReview: () => void;
}

export interface OfferGroup {
  player: Player;
  offers: ReceivedOffer[];
}

interface ReceivedOffersListProps {
  title: string;
  subtitle?: string;
  groups: OfferGroup[];
  emptyMessage?: string;
  onMemberClick?: (fantasyLeagueMemberId: string) => void;
}

export const ReceivedOffersList = ({
  title,
  subtitle,
  groups,
  emptyMessage,
  onMemberClick,
}: ReceivedOffersListProps) => {
  if (groups.length === 0) {
    if (!emptyMessage) return null;
    return (
      <div className="text-center mt-4 py-4">
        <p className="text-gray-500 text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="px-2">
        <h2 className="text-lg font-semibold">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>

      {groups.map(({ player, offers }) => (
        <div key={player.id} className="mb-6">
          <PlayerCard
            player={player}
            footerDetails={
              <div className="flex flex-col gap-3">
                {offers.map((offer, index) => (
                  <div key={offer.id}>
                    <div className="flex items-center justify-between gap-3">
                      {offer.bidderMemberId && onMemberClick ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onMemberClick(offer.bidderMemberId!);
                          }}
                          aria-label={`Ver plantilla de ${offer.bidderName}`}
                          className="flex min-w-0 items-center gap-1.5 text-slate-600 transition-colors hover:text-[var(--fc-green)]"
                        >
                          <IonIcon icon={personOutline} className="shrink-0 text-sm" />
                          <span className="truncate">{offer.bidderName}</span>
                        </button>
                      ) : (
                        <div className="flex min-w-0 items-center gap-1.5 text-slate-600">
                          <IonIcon icon={personOutline} className="shrink-0 text-sm" />
                          <span className="truncate">{offer.bidderName}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <div className="rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-900">
                          {offer.amount.toLocaleString('es-ES')}€
                        </div>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            offer.onReview();
                          }}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:rgba(var(--ion-color-primary-rgb),0.2)] bg-[rgba(var(--ion-color-primary-rgb),0.08)] text-[var(--ion-color-primary-shade)] transition-colors hover:bg-[rgba(var(--ion-color-primary-rgb),0.14)]"
                          aria-label="Ver oferta"
                          data-testid={`review-offer-${offer.id}`}
                        >
                          <IonIcon icon={eye} className="text-lg" />
                        </button>
                      </div>
                    </div>

                    {index < offers.length - 1 && (
                      <div className="mt-3 w-full border-b border-gray-200" />
                    )}
                  </div>
                ))}
              </div>
            }
          />
        </div>
      ))}
    </div>
  );
};
